import { useState, useEffect, useCallback } from 'react';
import {
  CalendarClock, Check, X, Clock, User, MapPin, AlertCircle, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { jobService } from '../../../services/jobService';
import { formatDate } from '../../../utils/formatters';

/**
 * ExtensionRequestsPanel
 *
 * Corporate panel for reviewing and managing extension requests from contractors.
 * Can be used as a standalone component or embedded in the Reports page.
 */
export default function ExtensionRequestsPanel({ companyId, onUpdate }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('pending'); // 'pending' | 'all'

  // Fetch extension requests
  const fetchRequests = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError('');
      const status = filter === 'pending' ? 'pending' : null;
      const data = await jobService.getExtensionRequests(companyId, status);
      setRequests(data || []);
    } catch (err) {
      console.error('Error fetching extension requests:', err);
      setError('Failed to load extension requests');
    } finally {
      setLoading(false);
    }
  }, [companyId, filter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Handle approve/deny
  const handleReview = async (requestId, approved) => {
    try {
      setProcessingId(requestId);
      await jobService.reviewExtensionRequest(requestId, approved, reviewNote || null);
      setReviewNote('');
      setExpandedId(null);
      await fetchRequests();
      onUpdate?.();
    } catch (err) {
      console.error('Error reviewing request:', err);
      setError(`Failed to ${approved ? 'approve' : 'deny'} request`);
    } finally {
      setProcessingId(null);
    }
  };

  // Get status badge styles
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      denied: 'bg-red-500/20 text-red-400 border-red-500/30'
    };

    const labels = {
      pending: 'Pending',
      approved: 'Approved',
      denied: 'Denied'
    };

    return (
      <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full border', styles[status])}>
        {labels[status] || status}
      </span>
    );
  };

  // Calculate time since request
  const getTimeSince = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
            <CalendarClock className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Extension Requests</h3>
            <p className="text-sm text-text-secondary">
              {pendingCount > 0
                ? `${pendingCount} pending request${pendingCount > 1 ? 's' : ''}`
                : 'No pending requests'}
            </p>
          </div>
        </div>

        {/* Filter toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('pending')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
              filter === 'pending'
                ? 'bg-primary text-white'
                : 'bg-surface-light text-text-secondary hover:text-text-primary'
            )}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('all')}
            className={cn(
              'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
              filter === 'all'
                ? 'bg-primary text-white'
                : 'bg-surface-light text-text-secondary hover:text-text-primary'
            )}
          >
            All
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && requests.length === 0 && (
          <div className="py-8 text-center">
            <CalendarClock className="w-12 h-12 text-text-tertiary mx-auto mb-3" />
            <p className="text-text-secondary">
              {filter === 'pending'
                ? 'No pending extension requests'
                : 'No extension requests found'}
            </p>
          </div>
        )}

        {/* Requests list */}
        {!loading && !error && requests.length > 0 && (
          <div className="space-y-3">
            {requests.map((request) => {
              const isExpanded = expandedId === request.id;
              const isProcessing = processingId === request.id;
              const isPending = request.status === 'pending';
              const jobInstance = request.job_instance;
              const jobDetails = jobInstance?.jobs;
              const contractor = request.contractor;

              return (
                <div
                  key={request.id}
                  className={cn(
                    'bg-background border rounded-xl overflow-hidden transition-all',
                    isPending ? 'border-orange-500/30' : 'border-border'
                  )}
                >
                  {/* Main row - clickable */}
                  <div
                    onClick={() => setExpandedId(isExpanded ? null : request.id)}
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-light transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Contractor avatar */}
                      <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-primary" />
                      </div>

                      {/* Request info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-text-primary">
                            {contractor?.full_name || 'Unknown Contractor'}
                          </span>
                          {getStatusBadge(request.status)}
                        </div>
                        <p className="text-sm text-text-secondary truncate">
                          {jobDetails?.job_name || 'Unknown Job'}
                          {jobInstance?.instance_number && ` #${jobInstance.instance_number}`}
                        </p>
                        <p className="text-xs text-text-tertiary">
                          {getTimeSince(request.created_at)}
                        </p>
                      </div>
                    </div>

                    {/* Expand indicator */}
                    <div className="flex-shrink-0 ml-4">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-text-secondary" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-text-secondary" />
                      )}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 space-y-4 border-t border-border">
                      {/* Request details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                        <div>
                          <p className="text-xs text-text-tertiary mb-1">Scheduled Date</p>
                          <p className="text-sm text-text-primary">
                            {formatDate(jobInstance?.scheduled_date) || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-text-tertiary mb-1">Requested Deadline</p>
                          <p className="text-sm text-text-primary font-medium">
                            {new Date(request.requested_deadline).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Reason */}
                      <div>
                        <p className="text-xs text-text-tertiary mb-1">Reason for Extension</p>
                        <p className="text-sm text-text-primary bg-surface p-3 rounded-lg">
                          {request.reason}
                        </p>
                      </div>

                      {/* Review note input (for pending) */}
                      {isPending && (
                        <div>
                          <label className="block text-xs text-text-tertiary mb-1">
                            Response note (optional)
                          </label>
                          <textarea
                            value={reviewNote}
                            onChange={(e) => setReviewNote(e.target.value)}
                            placeholder="Add a note for the contractor..."
                            rows={2}
                            className={cn(
                              'w-full px-3 py-2 rounded-lg resize-none text-sm',
                              'bg-surface border border-border',
                              'text-text-primary placeholder-text-tertiary',
                              'focus:outline-none focus:border-primary'
                            )}
                          />
                        </div>
                      )}

                      {/* Actions (for pending) */}
                      {isPending && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleReview(request.id, true)}
                            disabled={isProcessing}
                            className={cn(
                              'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors',
                              'bg-green-500 hover:bg-green-600 text-white',
                              'disabled:opacity-50 disabled:cursor-not-allowed'
                            )}
                          >
                            {isProcessing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => handleReview(request.id, false)}
                            disabled={isProcessing}
                            className={cn(
                              'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors',
                              'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30',
                              'disabled:opacity-50 disabled:cursor-not-allowed'
                            )}
                          >
                            {isProcessing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            Deny
                          </button>
                        </div>
                      )}

                      {/* Review details (for reviewed requests) */}
                      {!isPending && (
                        <div className="pt-2 border-t border-border">
                          <p className="text-xs text-text-tertiary">
                            {request.status === 'approved' ? 'Approved' : 'Denied'} on{' '}
                            {new Date(request.reviewed_at).toLocaleString()}
                          </p>
                          {request.review_note && (
                            <p className="text-sm text-text-secondary mt-1">
                              Note: {request.review_note}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
